import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import * as yup from 'yup';
import Form, { SubmitType } from '../../../components/form';
import Group from '../../../components/form/elements/group';
import Button from '../../../components/form/elements/button';
import Link from '../../../components/form/elements/link';
import Date from '../../../components/form/elements/date';
import Text from '../../../components/form/elements/text';
import Decimal from '../../../components/form/elements/decimal';
import Select, { Option } from '../../../components/form/elements/select';
import Maintenance from '../../../interfaces/maintenance';
import Paginate from '../../../interfaces/paginate';
import Employee from '../../../interfaces/employee';
import { AllEquipmentFormFields } from '../../../interfaces/equipment';
import { numberTransform, route, when } from '../../../services/utils';
import { useService } from '../../../services/services';
import {
  EQUIPMENT_MAINTENANCE_INDEX,
  EQUIPMENT_MAINTENANCE_VIEW,
} from '../../../router/routes';

const schema = yup.object().shape({
  date: yup.date().required(),
  equipment_id: yup.object().shape({
    value: yup.string().required().uuid(),
  }),
  name: yup.string().max(255).required(),
  employee_id: when(
    (value) => !!value?.value,
    yup.object().shape({
      value: yup.string().uuid(),
    }),
    yup.object().nullable()
  ),
  spent_time: yup
    .number()
    .positive()
    .decimal()
    .nullable()
    .transform(numberTransform),
  used_materials: yup.string().max(255).nullable(),
  material_cost: yup
    .number()
    .positive()
    .decimal()
    .nullable()
    .transform(numberTransform),
});

export default function CreateMaintenance() {
  const { t } = useTranslation();
  const history = useHistory();
  const { farmId } = useParams<{ farmId: string }>();
  const { maintenanceService, equipmentService, employeeService } =
    useService();

  const transKeyPrefix = 'pages.equipment.maintenance.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const submit = useCallback<SubmitType<Maintenance>>(
    (form) => {
      form.equipment_id = (form.equipment_id as unknown as Option)?.value;
      form.employee_id = (form.employee_id as unknown as Option)?.value;

      return [
        maintenanceService.store(farmId, form),
        (response) =>
          history.push(
            route(EQUIPMENT_MAINTENANCE_VIEW, { farmId, id: response.id })
          ),
      ];
    },
    [maintenanceService, history, farmId]
  );

  const [body] = useState(() => [
    Group([
      Date('date', transKeyPrefix + 'date'),
      Select('equipment_id', {
        asyncOptions: (value, loadedOptions, { page }) => {
          return equipmentService.index(farmId, { page }).pipe(
            map((response: Paginate<AllEquipmentFormFields>) => {
              return {
                hasMore: response.last_page > response.current_page,
                options: response.data.map((equipment) => ({
                  value: equipment.id,
                  label: equipment.label,
                })),
                additional: { page: response.current_page + 1 },
              };
            })
          );
        },
        label: transKeyPrefix + 'equipment',
      }),
      Text('name', transKeyPrefix + 'name'),
      Select('employee_id', {
        asyncOptions: (value, loadedOptions, { page }) => {
          return employeeService.index(farmId, { page }).pipe(
            map((response: Paginate<Employee>) => {
              return {
                hasMore: response.last_page > response.current_page,
                options: response.data.map((employee) => ({
                  label: employee.first_name.concat(' ', employee.last_name),
                  value: employee.id,
                })),
                additional: { page: response.current_page + 1 },
              };
            })
          );
        },
        label: transKeyPrefix + 'employee',
      }),
      Decimal('spent_time', transKeyPrefix + 'spent_time'),
      Text('used_materials', transKeyPrefix + 'used_materials'),
      Decimal('material_cost', transKeyPrefix + 'material_cost'),
    ]),
  ]);

  const [footer] = useState(() => [
    Button('create'),
    Link(route(EQUIPMENT_MAINTENANCE_INDEX, { farmId }), 'cancel'),
  ]);

  return (
    <Form
      title={tp('create')}
      mode="create"
      body={body}
      footer={footer}
      submit={submit}
      schema={schema}
    />
  );
}
